import React, { useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, Stack, Typography, useMediaQuery } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { CommonDialogTitle } from '../CommonDialogTitle';
import { PaperComponent } from '../PaperComponent';

import { DayPicker, TZDate } from 'react-day-picker';
import {
  createTZDateWithTime,
  dateRangeToTZDateRange,
  formatDayInTimeZone,
  getDateRangePast7Days,
  getDateRangeToday,
  getTimeZoneAbbreviation,
  getTimeZoneIana,
} from '../../utils/dateTimeUtils';

// /////////////////////////////////////////////////////////////////////////////////////////////////
export const DateRangeEntry = (props) => {
  const { value, onChange, sx, showDates = true, compact, timezoneIana, showCalendarIcon = true } = props;
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const timezoneIanaToUse = timezoneIana ?? getTimeZoneIana(); // using local tz as default

  const [open, setOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(dateRangeToTZDateRange(value, timezoneIanaToUse));

  // keep the calendar selection in sync with the external filter value
  // useEffect(() => {
  //   setSelectedRange(dateRangeToTZDateRange(value, timezoneIanaToUse));
  // }, [value?.from, value?.to, timezoneIanaToUse]);

  const handleClickOpen = () => {
    // ensure the latest filter value is reflected when the dialog opens
    setSelectedRange(dateRangeToTZDateRange(value, timezoneIanaToUse));
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleApply = () => {
    if (!selectedRange?.from) {
      onChange(undefined);
      setOpen(false);
      return;
    }
    // Clamp inside the chosen IANA zone to avoid off-by-one
    const from = createTZDateWithTime(selectedRange.from, timezoneIanaToUse, 0, 0, 0, 0);
    const toRaw = selectedRange.to ?? selectedRange.from;
    const to = createTZDateWithTime(toRaw, timezoneIanaToUse, 23, 59, 59, 999);

    if (to.getTime() < from.getTime()) {
      onChange({ from, to: createTZDateWithTime(from, timezoneIanaToUse, 23, 59, 59, 999) });
    } else {
      onChange({ from, to });
    }
    setOpen(false);
  };

  const modifiers = {
    start: selectedRange?.from,
    end: selectedRange?.to,
  };

  const getDateString = (date) => {
    if (date) {
      const tzInfo = { iana: timezoneIanaToUse, local: false };
      return formatDayInTimeZone(date, tzInfo, false);
    } else {
      return 'Not Set';
    }
  };

  const handleSetSelectedRange = (range) => {
    setSelectedRange(dateRangeToTZDateRange(range, timezoneIanaToUse));
  };

  const handlePresetDateClick = (e) => {
    const {
      target: { name },
    } = e;
    let to;
    let from;
    switch (name) {
      case 'today':
        handleSetSelectedRange(getDateRangeToday(timezoneIanaToUse));
        break;
      case 'past24hrs':
        to = new Date();
        from = new Date(Date.now() - 1 * 24 * 3600 * 1000);
        handleSetSelectedRange({ from, to });
        break;
      case 'past7days':
        handleSetSelectedRange(getDateRangePast7Days(timezoneIanaToUse));
        break;
      case 'weektodate':
        {
          const tzNow = new TZDate(new Date(), timezoneIanaToUse);
          to = new TZDate(tzNow, timezoneIanaToUse);
          to.setHours(23, 59, 59, 999);
          const day = tzNow.getDay();
          const diff = (day === 0 ? -6 : 1) - day; // Adjust when day is Sunday
          const startOfWeek = new TZDate(tzNow, timezoneIanaToUse);
          startOfWeek.setHours(0, 0, 0, 0);
          startOfWeek.setDate(startOfWeek.getDate() + diff);
          handleSetSelectedRange({ from: startOfWeek, to });
        }
        break;
      case 'monthtodate':
        {
          const tzNow = new TZDate(new Date(), timezoneIanaToUse);
          to = new TZDate(tzNow, timezoneIanaToUse);
          to.setHours(23, 59, 59, 999);
          from = new TZDate(tzNow, timezoneIanaToUse);
          from.setDate(1);
          from.setHours(0, 0, 0, 0);
          handleSetSelectedRange({ from, to });
        }
        break;
      case 'alltimes':
        setSelectedRange(undefined);
        break;
      default:
        console.warn('Error: unknown button pressed', name, e);
        break;
    }
  };

  const startDate = getDateString(value?.from);
  const endDate = getDateString(value?.to);

  return (
    <>
      <Button
        id='date-range-entry'
        color='controls'
        variant={showCalendarIcon ? 'outlined' : 'text'}
        onClick={handleClickOpen}
        sx={[
          {
            height: { md: '40px', xs: '32px' },
            minWidth: 'fit-content',
            p: '5px 8px',
            color: (theme) => theme.palette.primary.main,
            '&:hover': { borderColor: (theme) => theme.palette.primary.main },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {showCalendarIcon && <CalendarTodayOutlinedIcon sx={{ fontSize: '20px', mr: showDates ? 1 : 0 }} />}
        {showDates ? (
          <Typography variant={compact ? 'body2' : 'body1'}>
            {startDate} &#x2014; {endDate}
          </Typography>
        ) : null}
      </Button>
      <Dialog
        PaperComponent={!isMobile ? PaperComponent : null}
        aria-labelledby='draggable-dialog-title'
        onClose={handleClose}
        open={open}
        maxWidth='lg'
      >
        <CommonDialogTitle
          title={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mr: 2 }}>
              <Box>Select a Date Range</Box>
              <Typography sx={{ ml: 'auto' }}>{getTimeZoneAbbreviation(timezoneIanaToUse)}</Typography>
            </Box>
          }
          handleClose={handleClose}
        />
        <DialogContent dividers sx={{ backgroundColor: (theme) => theme.palette.background.main }}>
          <Stack direction={isMobile ? 'column' : 'row'} justifyContent='space-between' spacing={2}>
            <Stack spacing={1}>
              <Button variant='contained' color='tertiaryBtn' name='today' onClick={handlePresetDateClick}>
                Today
              </Button>
              <Button variant='contained' color='tertiaryBtn' name='past24hrs' onClick={handlePresetDateClick}>
                Past 24 hrs
              </Button>
              <Button variant='contained' color='tertiaryBtn' name='past7days' onClick={handlePresetDateClick}>
                Past 7 Days
              </Button>
              <Button variant='contained' color='tertiaryBtn' name='weektodate' onClick={handlePresetDateClick}>
                Week to Date
              </Button>
              <Button variant='contained' color='tertiaryBtn' name='monthtodate' onClick={handlePresetDateClick}>
                Month to Date
              </Button>
              <Button variant='contained' color='tertiaryBtn' name='alltimes' onClick={handlePresetDateClick}>
                All Times
              </Button>
            </Stack>
            <Box
              sx={{
                '.rdp-months': {
                  display: 'flex',
                  justifyContent: 'center',
                },
                '@media (max-width: 600px)': {
                  '.rdp-month': {
                    transform: 'scale(0.9)',
                  },
                },
              }}
            >
              <DayPicker
                mode='range'
                captionLayout='dropdown'
                className='Selectable'
                numberOfMonths={2}
                selected={selectedRange}
                onSelect={setSelectedRange}
                modifiers={modifiers}
                timeZone={timezoneIanaToUse}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', backgroundColor: (theme) => theme.palette.background.main }}>
          <Button variant='outlined' autoFocus onClick={handleClose} sx={{ width: '100%', '& .MuiButton-root': { width: '50%' } }}>
            Cancel
          </Button>
          <Button variant='contained' autoFocus onClick={handleApply} sx={{ width: '100%', '& .MuiButton-root': { width: '50%' } }}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
